// src/services/matchService.js

import { db } from './firebase';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  setDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

import { getAtivoById } from './ativosService';

// ✅ engine roda no BACKEND (Functions callable)
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

/* =========================================================
   MATCH SERVICE — FRONT (build-safe)
   - Firestore -> MatchRequest -> chama Backend (matchGerar) -> grava matches
   - Subcoleção: negocios/{negocioId}/matches
   - ✅ Sem fallback chatElion
========================================================= */

const REGION = 'us-central1';

function toNumber(val, fallback = 0) {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function upperStatus(s) {
  const x = String(s || '').toUpperCase();
  if (x === 'VERDE' || x === 'AMARELO' || x === 'VERMELHO') return x;
  return 'AMARELO';
}

function docIdSafe(str) {
  return String(str || '')
    .replaceAll('/', '_')
    .replaceAll('#', '_')
    .replaceAll('?', '_')
    .replaceAll('&', '_')
    .replaceAll('=', '_')
    .slice(0, 220);
}

function buildMatchDocId(r) {
  const tipo = String(r?.tipo_match || r?.tipo || 'MATCH');
  const im = String(
    r?.imovel_doc_id || r?.imovelA_id || r?.imovelAId || 'imovel'
  );
  const b = String(r?.item_b_id || r?.itemB_id || r?.itemBId || 'b');
  const c = String(r?.item_c_id || r?.itemC_id || r?.itemCId || 'c');
  return docIdSafe(`${tipo}__${im}__${b}__${c}`);
}

/* =========================================================
   Ler negócio (público + privado opcional)
========================================================= */

export async function getNegocioById(negocioId) {
  if (!negocioId) throw new Error('negocioId ausente');
  const snap = await getDoc(doc(db, 'negocios', negocioId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getNegocioPrivado(negocioId) {
  if (!negocioId) return null;
  const snap = await getDoc(doc(db, 'negocios', negocioId, 'privado', 'dados'));
  return snap.exists() ? snap.data() : null;
}

/* =========================================================
   Adapters locais (sem importar schemas do backend)
========================================================= */

function permutaItemToEngineItemLocal(it, ownerId, idx = 0) {
  const tipo = String(it?.tipo || 'OUTRO').toUpperCase();
  const key = String(it?.key || `${tipo}_${idx}`);
  const valor = toNumber(it?.valorAproximado ?? it?.valor ?? 0, 0);

  return {
    id: `permuta_${docIdSafe(key)}`,
    owner_id: ownerId || 'cliente_b',
    tipo: 'ATIVO',
    subtipo: tipo,
    valor_referencia: valor,
    valor_troca: valor,
    liquidez: null,
    documentos: null,
    metadata: {
      origem: 'PERMUTA_LEGADO',
      key,
    },
  };
}

/* =========================================================
   Montar MatchRequest (novo contrato) + fallback legado
========================================================= */

async function resolveItensDisponiveisEngine(negocio) {
  // ✅ novo padrão (preferido): itens_disponiveis_ids[]
  const ids = safeArray(negocio?.itens_disponiveis_ids);

  if (ids.length) {
    const ativos = [];
    for (const id of ids) {
      try {
        const a = await getAtivoById(id);
        if (a) ativos.push(a);
      } catch (_) {
        // ignora ativo inválido
      }
    }

    const engineItems = ativos
      .map((a) => ({
        id: a.id,
        owner_id: a.owner_id || a.corretorId || 'owner_b',
        tipo: a.tipo || 'ATIVO',
        subtipo: a.subtipo || a.itemTipo || 'OUTRO',
        valor_referencia: toNumber(a.valor_referencia ?? a.valor, 0),
        valor_troca: toNumber(a.valor_troca ?? a.valor, 0),
        liquidez: a.liquidez || a.liquidez_nivel || null,
        documentos: a.documentos || null,
        metadata: a.metadata || { origem: 'ATIVO' },
      }))
      .filter((x) => toNumber(x.valor_troca, 0) > 0);

    return engineItems;
  }

  // ✅ fallback legado: negocio.permuta.itens
  const itensLegado = safeArray(negocio?.permuta?.itens);
  if (itensLegado.length) {
    const compradorId = negocio?.clienteId || negocio?.id || 'cliente_b';
    return itensLegado
      .map((it, idx) =>
        permutaItemToEngineItemLocal(
          {
            key: it?.key,
            tipo: it?.tipo,
            valorAproximado: toNumber(it?.valorAproximado, 0),
          },
          compradorId,
          idx
        )
      )
      .filter((x) => toNumber(x?.valor_troca, 0) > 0);
  }

  return [];
}

function buildRequestFromNegocio(negocio, itensDisponiveisEngine) {
  return {
    request_id: negocio.id,
    corretor_id: negocio.corretorId || null,

    // alvo (se já existir no doc)
    alvo_id_imovel: negocio.alvo_id_imovel || negocio.alvoIdImovel || null,

    // filtros (fallback quando não há alvo)
    filtros: {
      cidade: negocio.cidadePrincipal || negocio.cidade || null,
      tipoImovel: negocio.tipoImovel || null,
      valorMin: toNumber(negocio.valorMin, 0) || null,
      valorMax: toNumber(negocio.valorMax, 0) || null,
      bairrosSlug: safeArray(negocio.bairrosSlug),
    },

    itens_disponiveis: itensDisponiveisEngine,
    entrada_disponivel: toNumber(negocio.entrada_disponivel, 0) || 0,
    observacoes_corretor: negocio.observacoes_corretor || '',
    permitir_triangular: true,
  };
}

/* =========================================================
   Backend call (Callable)
========================================================= */

async function callMatchBackend(payload) {
  const functions = getFunctions(getApp(), REGION);

  try {
    const fn = httpsCallable(functions, 'matchGerar');
    const res = await fn(payload);
    return res?.data;
  } catch (e) {
    const msg =
      e?.message ||
      'Falha ao chamar backend de match (matchGerar). Verifique deploy e região.';
    throw new Error(msg);
  }
}

/* =========================================================
   Salvar / Listar / Listen matches
========================================================= */

export async function salvarMatchResult(negocioId, matchResult) {
  if (!negocioId) throw new Error('negocioId ausente');
  if (!matchResult) throw new Error('matchResult ausente');

  const docId = buildMatchDocId(matchResult);
  const ref = doc(db, 'negocios', negocioId, 'matches', docId);

  // ✅ preserva createdAt: só seta no primeiro save
  const existsSnap = await getDoc(ref);
  const isNew = !existsSnap.exists();

  const payload = {
    ...matchResult,
    status: upperStatus(matchResult?.status),
    score_match: toNumber(matchResult?.score_match, 0),
    negocio_id: negocioId,
    updatedAt: serverTimestamp(),
    ...(isNew ? { createdAt: serverTimestamp() } : {}),
  };

  await setDoc(ref, payload, { merge: true });
  return { id: docId, ...payload };
}

/**
 * ✅ Batch save (muito mais rápido que salvar 1 por 1)
 * - preserva createdAt: se doc já existe, não seta createdAt.
 */
async function salvarMatchesBatch(negocioId, matchResults = []) {
  const list = safeArray(matchResults);
  if (!negocioId) throw new Error('negocioId ausente');
  if (!list.length) return [];

  const batch = writeBatch(db);

  // 1) descobrir quais docIds já existem (pra não regravar createdAt)
  const refs = list.map((r) => {
    const docId = buildMatchDocId(r);
    return { docId, ref: doc(db, 'negocios', negocioId, 'matches', docId), r };
  });

  const snaps = await Promise.all(refs.map((x) => getDoc(x.ref)));

  const saved = [];
  refs.forEach((x, idx) => {
    const exists = snaps[idx]?.exists?.() === true;

    const payload = {
      ...x.r,
      status: upperStatus(x.r?.status),
      score_match: toNumber(x.r?.score_match, 0),
      negocio_id: negocioId,
      updatedAt: serverTimestamp(),
      ...(exists ? {} : { createdAt: serverTimestamp() }),
    };

    batch.set(x.ref, payload, { merge: true });
    saved.push({ id: x.docId, ...payload });
  });

  await batch.commit();
  return saved;
}

export async function listarMatchesDoNegocio(negocioId, max = 30) {
  if (!negocioId) return [];
  const q = query(
    collection(db, 'negocios', negocioId, 'matches'),
    orderBy('updatedAt', 'desc'),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function listenMatchesDoNegocio(negocioId, cb, max = 30) {
  if (!negocioId) return () => {};
  const q = query(
    collection(db, 'negocios', negocioId, 'matches'),
    orderBy('updatedAt', 'desc'),
    limit(max)
  );

  return onSnapshot(
    q,
    (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cb(lista);
    },
    (err) => {
      console.error('[listenMatchesDoNegocio] erro:', err);
      cb([]); // evita quebrar UI
    }
  );
}

/* =========================================================
   GERAR (chama backend + salva no Firestore)
========================================================= */

export async function gerarMatchesParaNegocio(
  negocioId,
  { maxResultados = 12, permitirTriangular = true, catalogoC = [] } = {}
) {
  const negocio = await getNegocioById(negocioId);
  if (!negocio) return { negocio: null, resultados: [] };

  // regra básica: se não aceita permuta, não gera
  const aceitaPermuta =
    !!negocio?.permuta?.aceita ||
    safeArray(negocio?.itens_disponiveis_ids).length > 0;

  if (!aceitaPermuta) return { negocio, resultados: [] };

  // itens B
  const itensDisponiveis = await resolveItensDisponiveisEngine(negocio);
  if (!itensDisponiveis.length) return { negocio, resultados: [] };

  // montar MatchRequest
  const request = buildRequestFromNegocio(negocio, itensDisponiveis);

  // catálogo C (se não vier, usa os próprios itens disponíveis como fallback)
  const catalogoCFinal =
    safeArray(catalogoC).length > 0 ? safeArray(catalogoC) : itensDisponiveis;

  // ✅ chama backend
  const data = await callMatchBackend({
    request,
    catalogoC: catalogoCFinal,
    options: {
      permitirTriangular,
      maxResultados,
    },
  });

  // aceita vários formatos de retorno
  const resultadosRaw =
    safeArray(data?.resultados).length > 0
      ? data.resultados
      : safeArray(data?.matches).length > 0
        ? data.matches
        : safeArray(data).length > 0
          ? data
          : [];

  // limita
  const finais = safeArray(resultadosRaw).slice(0, maxResultados);

  // ✅ salva em batch (mais rápido)
  await salvarMatchesBatch(negocioId, finais);

  return { negocio, resultados: finais };
}
